/**
 * Página CRUD genérica: tabla + modal create/edit + confirmación delete.
 *
 * Props:
 *  title       — título de la sección
 *  columns     — [{ key, label, render? }] columnas de la tabla
 *  fields      — [{ key, label, type, options?, required? }] campos del formulario
 *  fetchAll    — async () => []
 *  onCreate    — async (data) => {}
 *  onUpdate    — async (id, data) => {}
 *  onDelete    — async (id) => {}
 *  idKey       — nombre del campo ID (default 'ID')
 *  actions?    — [{ label, onClick(row) }] acciones adicionales por fila
 *  deleteLabel?       — texto del botón/ícono de borrado (default 'Eliminar')
 *  deleteConfirmText? — texto de confirmación (default advierte que no se puede deshacer)
 */
import { Fragment, useEffect, useState, useCallback, useRef } from 'react';
import { Pencil, Trash2, ChevronRight, Plus, Search, Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight } from 'lucide-react';
import { sanitizeHtml } from '../../../lib/sanitizeHtml';

export default function CrudPage({
  title, columns, fields, fetchAll, onCreate, onUpdate, onDelete,
  idKey = 'ID', actions = [], toolbar, filterFn, modalCols = 1,
  defaultSort = null, rowClassName, titleExtra, itemLabel = 'registros',
  deleteLabel = 'Eliminar',
  deleteConfirmText = '¿Eliminar este registro? Esta acción no se puede deshacer.',
  canDelete = () => true,
  onCreated,
}) {
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [search, setSearch]     = useState('');
  const [modal, setModal]       = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving]     = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [fieldErrors, setFieldErrors]   = useState({});
  const [sort, setSort]         = useState(defaultSort ?? { key: null, dir: 'asc' });

  const toggleSort = (key) =>
    setSort((s) => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });

  /** ID siempre ordenable; otras columnas solo si marcan sortable: true */
  const isColSortable = (c) =>
    c.sortable === true || (c.sortable !== false && c.key === idKey);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try { setRows(await fetchAll()); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [fetchAll]);

  useEffect(() => { load(); }, [load]);

  const resolveFields = (row) => {
    const resolved = typeof fields === 'function' ? fields(row) : fields;
    return Array.isArray(resolved) ? resolved : [];
  };

  const openCreate = () => {
    const empty = {};
    const fArr = Array.isArray(fields) ? fields : [];
    fArr.forEach((field) => {
      if (field.type === 'flag-group') {
        (field.items ?? []).forEach((item) => { empty[item.key] = false; });
      } else if (typeof field.defaultValue === 'function') {
        empty[field.key] = field.defaultValue(rows);
      } else {
        empty[field.key] = field.defaultValue ?? (field.type === 'checkbox' ? false : '');
      }
    });
    setFormData(empty);
    setFieldErrors({});
    setModal({ mode: 'create' });
  };

  const openEdit = (row) => {
    const f = resolveFields(row);
    const data = {};
    f.forEach((field) => {
      if (field.type === 'flag-group') {
        (field.items ?? []).forEach((item) => { data[item.key] = !!row[item.key]; });
      } else {
        data[field.key] = row[field.key] ?? (field.type === 'checkbox' ? false : '');
      }
    });
    setFormData(data);
    setFieldErrors({});
    setModal({ mode: 'edit', id: row[idKey], row });
  };

  const handleSave = async () => {
    const activeFields = resolveFields(modal?.row);
    const errors = {};
    activeFields.forEach((f) => {
      if (f.required && !formData[f.key]?.toString().trim()) errors[f.key] = 'Campo requerido';
    });
    if (Object.keys(errors).length) { setFieldErrors(errors); return; }
    setFieldErrors({});
    setSaving(true);
    try {
      if (modal.mode === 'create') {
        const created = await onCreate(formData);
        setModal(null);
        if (onCreated) { onCreated(created, formData); return; } // el caller decide navegar/refrescar
      } else {
        await onUpdate(modal.id, formData, modal.row);
        setModal(null);
      }
      load();
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await onDelete(deleteTarget[idKey], deleteTarget);
      setDeleteTarget(null);
      load();
    } catch (e) {
      alert(e.message);
    }
  };

  const norm = (s) => String(s ?? '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  const filtered = filterFn ? rows.filter(filterFn) : rows;
  const rawQ = search.trim();
  const q = norm(rawQ);
  const idQuery = /^\d+$/.test(rawQ);
  const searched = !rawQ
    ? filtered
    : filtered.filter((row) => {
        // Si el texto es solo dígitos, buscar por ID (coincidencia parcial)
        if (idQuery) return String(row[idKey] ?? '').includes(rawQ);
        if (columns.some((c) => c.key !== '_idx' && norm(String(row[c.key] ?? '')).includes(q))) return true;
        // Incluir idKey aunque no esté visible en columnas (p. ej. Usuarios)
        return norm(String(row[idKey] ?? '')).includes(q);
      });
  const visibleRows = sort.key
    ? [...searched].sort((a, b) => {
        const av = a[sort.key], bv = b[sort.key];
        const an = Number(av), bn = Number(bv);
        const bothNumeric = Number.isFinite(an) && Number.isFinite(bn)
          && String(av ?? '').trim() !== '' && String(bv ?? '').trim() !== '';
        const cmp = bothNumeric
          ? an - bn
          : String(av ?? '').localeCompare(String(bv ?? ''), 'es', { numeric: true });
        return sort.dir === 'asc' ? cmp : -cmp;
      })
    : searched;

  return (
    <div className="h-full flex flex-col p-6">
      <div className="flex-shrink-0 flex items-center justify-between mb-4 gap-4 flex-wrap">
        <h1 className="text-2xl font-bold text-slate-800">{title}</h1>
        <div className="flex items-center gap-2 flex-wrap">
          {titleExtra}
          {onCreate && (
            <button
              onClick={openCreate}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium"
            >
              <Plus size={15} />
              Nuevo
            </button>
          )}
        </div>
      </div>

      <div className="flex-shrink-0 flex flex-wrap items-center justify-between gap-3 mb-4 bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              placeholder="Buscar..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="rounded-lg border border-slate-300 pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-violet-500 w-full max-w-xs"
            />
          </div>
          <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-violet-50 text-violet-700 text-xs font-medium whitespace-nowrap">
            {q
              ? <>{visibleRows.length} resultado{visibleRows.length === 1 ? '' : 's'} para la búsqueda "<em>{search.trim()}</em>"</>
              : <><strong>{visibleRows.length}</strong>&nbsp;{itemLabel}</>}
          </span>
        </div>
        {toolbar && <div className="flex flex-wrap items-center gap-3">{toolbar}</div>}
      </div>

      {error && <p className="flex-shrink-0 text-red-500 mb-4 text-sm">{error}</p>}

      <div className="flex-1 min-h-0 overflow-auto bg-white rounded-xl shadow-sm">
        {loading ? (
          <p className="text-slate-500 text-sm p-4">Cargando...</p>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
                <tr>
                  {columns.map((c) => {
                    const sortable = isColSortable(c);
                    return (
                    <th
                      key={c.key}
                      onClick={sortable ? () => toggleSort(c.key) : undefined}
                      className={`px-4 py-3 text-left font-medium text-slate-600 whitespace-nowrap ${sortable ? 'cursor-pointer select-none hover:text-slate-900' : ''}`}
                    >
                      <span className="inline-flex items-center gap-1">
                        {c.label}
                        {sortable && (
                          <span className="text-[10px] leading-none">
                            {sort.key === c.key ? (sort.dir === 'asc' ? '↑' : '↓') : <span className="text-slate-300">↕</span>}
                          </span>
                        )}
                      </span>
                    </th>
                    );
                  })}
                  <th className="px-4 py-3 text-right font-medium text-slate-600">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {visibleRows.length === 0 && (
                  <tr><td colSpan={columns.length + 1} className="px-4 py-6 text-center text-slate-400">{rows.length === 0 ? 'Sin registros' : 'Sin resultados'}</td></tr>
                )}
                {visibleRows.map((row, i) => (
                  <tr key={row[idKey]} className={`hover:bg-slate-50 ${rowClassName ? rowClassName(row) : ''}`}>
                    {columns.map((c) => (
                      <td key={c.key} className={`px-4 py-3 ${c.primary ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>
                        {c.key === '_idx' ? i + 1 : c.render ? c.render(row[c.key], row) : String(row[c.key] ?? '')}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-1">
                        {actions.map((a) => (
                          <IconBtn
                            key={a.label}
                            title={a.label}
                            onClick={() => a.onClick(row)}
                            className="text-violet-600 hover:text-violet-800 hover:bg-violet-50"
                          >
                            <ChevronRight size={15} />
                          </IconBtn>
                        ))}
                        {onUpdate && (
                          <IconBtn title="Editar" onClick={() => openEdit(row)} className="text-slate-500 hover:text-slate-800 hover:bg-slate-100">
                            <Pencil size={14} />
                          </IconBtn>
                        )}
                        {onDelete && (
                          canDelete(row) ? (
                            <IconBtn title={deleteLabel} onClick={() => setDeleteTarget(row)} className="text-red-400 hover:text-red-600 hover:bg-red-50">
                              <Trash2 size={14} />
                            </IconBtn>
                          ) : (
                            <IconBtn title="No permitido" disabled className="text-slate-300 cursor-not-allowed">
                              <Trash2 size={14} />
                            </IconBtn>
                          )
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      {/* Modal create/edit */}
      {modal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">

          <div className={`bg-white rounded-2xl shadow-xl w-full flex flex-col max-h-[90vh] ${modalCols >= 2 ? 'max-w-2xl' : 'max-w-lg'}`}>

            <div className="px-6 py-4 border-b border-slate-200 flex-shrink-0">
              <h2 className="text-lg font-semibold text-slate-800">
                {modal.mode === 'create' ? 'Nuevo registro' : 'Editar registro'}
              </h2>
            </div>

            <div className={`px-6 py-5 overflow-y-auto flex-1 ${modalCols >= 2 ? 'grid grid-cols-2 gap-x-5 gap-y-4 content-start' : 'flex flex-col gap-4'}`}>
              {resolveFields(modal.row).map((f) => {
                if (f.type === 'flag-group') {
                  return (
                    <div key={f.key ?? '_flags'} className={modalCols >= 2 ? 'col-span-2' : ''}>
                      <label className="block text-sm font-medium text-slate-700 mb-2">{f.label}</label>
                      <FlagGroup
                        items={f.items}
                        formData={formData}
                        onChange={(k, v) => setFormData((d) => ({ ...d, [k]: v }))}
                      />
                    </div>
                  );
                }
                const spanClass = modalCols >= 2 && f.colSpan === 2 ? 'col-span-2' : '';
                const err = fieldErrors[f.key];
                return (
                  <div key={f.key} className={spanClass}>
                    <label className={`block text-sm font-medium mb-1 ${err ? 'text-red-600' : 'text-slate-700'}`}>
                      {f.label}{f.required && <span className="text-red-500 ml-0.5">*</span>}
                    </label>
                    <FieldInput
                      field={f}
                      value={formData[f.key]}
                      row={modal.row}
                      onChange={(v) => {
                        setFormData((d) => ({ ...d, [f.key]: v }));
                        if (err) setFieldErrors((e) => { const n = { ...e }; delete n[f.key]; return n; });
                      }}
                    />
                    {err && <p className="text-red-500 text-xs mt-1">{err}</p>}
                  </div>
                );
              })}

            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3 flex-shrink-0">
              <button
                onClick={() => setModal(null)}
                className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium disabled:opacity-50"
              >
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmación delete */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-2">Confirmar {deleteLabel.toLowerCase()}</h2>
            <p className="text-slate-600 text-sm mb-6">
              {deleteConfirmText}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-medium"
              >
                {deleteLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BreadcrumbSelect({ levels, value, onChange }) {
  const resolveSels = () => {
    if (!value) return Array(levels.length).fill(null);
    const sels = Array(levels.length).fill(null);
    sels[levels.length - 1] = value;
    for (let i = levels.length - 2; i >= 0; i--) {
      const child = (levels[i + 1]?.options ?? []).find(
        (o) => String(o.value) === String(sels[i + 1])
      );
      sels[i] = child?.parentId ?? null;
    }
    return sels;
  };

  const [sels, setSels] = useState(resolveSels);
  const [open, setOpen] = useState(value ? null : 0);

  const optionsAt = (i) => {
    const all = levels[i]?.options ?? [];
    if (i === 0) return all;
    return all.filter((o) => String(o.parentId) === String(sels[i - 1]));
  };

  const labelAt = (i) =>
    (levels[i]?.options ?? []).find((o) => String(o.value) === String(sels[i]))?.label ?? null;

  const pick = (i, val) => {
    const next = sels.map((s, j) => (j < i ? s : j === i ? val : null));
    setSels(next);
    if (i < levels.length - 1) {
      setOpen(i + 1);
    } else {
      onChange(val);
      setOpen(null);
    }
  };

  const jumpTo = (i) => {
    setSels(sels.map((s, j) => (j < i ? s : null)));
    onChange('');
    setOpen(i);
  };

  const clear = () => {
    setSels(Array(levels.length).fill(null));
    onChange('');
    setOpen(0);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center flex-wrap gap-1 min-h-7">
        {levels.map((lvl, i) => {
          if (i > 0 && sels[i - 1] === null) return null;
          const label = labelAt(i);
          return (
            <Fragment key={i}>
              {i > 0 && <span className="text-slate-300 text-sm select-none">›</span>}
              <button
                type="button"
                onClick={() => jumpTo(i)}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                  open === i
                    ? 'bg-violet-600 text-white border-violet-600'
                    : label
                    ? 'bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100'
                    : 'bg-slate-50 text-slate-400 border-slate-200'
                }`}
              >
                {label ?? lvl.label}
              </button>
            </Fragment>
          );
        })}
        {value && (
          <button
            type="button"
            onClick={clear}
            className="ml-1 text-slate-300 hover:text-red-400 text-sm leading-none"
          >
            ✕
          </button>
        )}
      </div>

      {open !== null && (
        <div className="border border-slate-200 rounded-lg overflow-hidden bg-white shadow-sm">
          <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-200 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
            Seleccionar {levels[open]?.label}
          </div>
          <div className="max-h-44 overflow-y-auto divide-y divide-slate-100">
            {optionsAt(open).length === 0 ? (
              <p className="text-slate-400 text-xs px-3 py-3 text-center">Sin opciones</p>
            ) : (
              optionsAt(open).map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => pick(open, opt.value)}
                  className={`w-full text-left text-sm px-3 py-2 transition-colors hover:bg-violet-50 hover:text-violet-700 ${
                    String(sels[open]) === String(opt.value)
                      ? 'bg-violet-50 text-violet-700 font-medium'
                      : 'text-slate-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PillSelect({ options, value, onChange }) {
  return (
    <div className="flex flex-wrap gap-2">
      {(options ?? []).map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors cursor-pointer select-none ${
            String(value) === String(opt.value)
              ? (opt.activeClass ?? 'bg-violet-600 text-white border-violet-600')
              : (opt.inactiveClass ?? 'bg-white text-slate-500 border-slate-300 hover:border-slate-400')
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function FlagGroup({ items, formData, onChange }) {
  return (
    <div className="flex flex-wrap gap-2">
      {(items ?? []).map((item) => (
        <button
          key={item.key}
          type="button"
          onClick={() => onChange(item.key, !formData[item.key])}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors cursor-pointer select-none ${
            formData[item.key]
              ? 'bg-violet-600 text-white border-violet-600'
              : 'bg-white text-slate-500 border-slate-300 hover:border-violet-400 hover:text-violet-600'
          }`}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

function ImageField({ value, onChange, onUpload, hint }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await onUpload(file);
      onChange(result.url);
    } catch (err) {
      alert(err.message);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {value && (
        <div className="flex items-center gap-3">
          <img src={value} alt="icono" className="w-14 h-14 rounded-lg object-cover border border-slate-200" />
          <button
            type="button"
            onClick={() => onChange('')}
            className="text-xs text-red-500 hover:text-red-700"
          >
            Quitar
          </button>
        </div>
      )}
      {!value && hint && (
        <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-md px-2 py-1">
          Sin imagen — nombre original sugerido: <span className="font-mono">{hint}</span>
        </p>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleFile}
        disabled={uploading}
        className="text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100 disabled:opacity-50"
      />
      {uploading && <span className="text-xs text-slate-500">Subiendo...</span>}
    </div>
  );
}

function groupFields(fields) {
  const groups = [];
  let inlineAcc = [];
  for (const f of fields) {
    if (f.inline) {
      inlineAcc.push(f);
    } else {
      if (inlineAcc.length) { groups.push({ inline: true, fields: inlineAcc }); inlineAcc = []; }
      groups.push({ inline: false, fields: [f] });
    }
  }
  if (inlineAcc.length) groups.push({ inline: true, fields: inlineAcc });
  return groups;
}

function FieldInput({ field, value, onChange, row }) {

  if (field.readonly) {
    const display = field.type === 'select'
      ? (field.options ?? []).find((o) => String(o.value) === String(value))?.label ?? value
      : value;
    return <p className="text-sm text-slate-700 py-1.5">{display ?? '—'}</p>;
  }
  if (field.type === 'switch') {
    return (
      <button
        type="button"
        role="switch"
        aria-checked={!!value}
        onClick={() => onChange(!value)}
        className={`relative inline-flex w-10 h-6 rounded-full transition-colors focus:outline-none ${value ? 'bg-red-500' : 'bg-slate-300'}`}
      >
        <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${value ? 'translate-x-4' : 'translate-x-0'}`} />
      </button>
    );
  }
  if (field.type === 'breadcrumb-select') {
    return <BreadcrumbSelect levels={field.levels ?? []} value={value} onChange={onChange} />;
  }
  if (field.type === 'pill-select') {
    return <PillSelect options={field.options} value={value} onChange={onChange} />;

  }
  if (field.type === 'image') {
    return <ImageField value={value} onChange={onChange} onUpload={field.onUpload} hint={field.hint ? field.hint(row) : null} />;
  }
  if (field.type === 'checkbox') {
    return (
      <input
        type="checkbox"
        checked={!!value}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 accent-violet-600"
      />
    );
  }
  if (field.type === 'select') {
    return (
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-violet-500"
      >
        <option value="">— Seleccionar —</option>
        {(field.options ?? []).map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    );
  }
  if (field.type === 'textarea') {
    return (
      <textarea
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-violet-500 resize-y"
      />
    );
  }
  if (field.type === 'richtext') {
    return <RichTextEditor value={value} onChange={onChange} />;
  }
  return (
    <input
      type={field.type ?? 'text'}
      value={value ?? ''}
      onChange={(e) => onChange(field.type === 'number' ? Number(e.target.value) : e.target.value)}
      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-violet-500"
    />
  );
}

const RICHTEXT_COLORS = ['#0f172a', '#dc2626', '#16a34a', '#2563eb', '#d97706', '#7c3aed'];

function ToolBtn({ title, onMouseDown, children }) {
  return (
    <button
      type="button"
      title={title}
      // mousedown (no click) para no perder la selección de texto antes de aplicar el comando
      onMouseDown={(e) => { e.preventDefault(); onMouseDown(); }}
      className="inline-flex items-center justify-center w-7 h-7 rounded-md border-0 bg-transparent text-slate-600 hover:bg-slate-100 transition-colors"
    >
      {children}
    </button>
  );
}

// Editor de texto enriquecido mínimo (negrita/cursiva/subrayado/color/alineación) para el
// campo "Valor" de Secuencias. Usa contentEditable + execCommand: deprecado pero sigue
// soportado en todos los navegadores actuales, y evita sumar una librería de edición completa
// para un caso de uso tan acotado. El HTML se sanitiza antes de subir cada cambio.
function RichTextEditor({ value, onChange }) {
  const ref = useRef(null);
  const skipNextSync = useRef(false);

  useEffect(() => {
    if (skipNextSync.current) { skipNextSync.current = false; return; }
    if (ref.current && ref.current.innerHTML !== (value ?? '')) {
      ref.current.innerHTML = value ?? '';
    }
  }, [value]);

  const emitChange = () => {
    skipNextSync.current = true;
    onChange(sanitizeHtml(ref.current?.innerHTML ?? ''));
  };

  const exec = (cmd, arg) => {
    ref.current?.focus();
    // Sin esto, foreColor/bold/italic generan <font>/<b>/<i> según el navegador; con
    // styleWithCSS todos usan <span style="..."> consistente con lo que sanitizeHtml permite.
    document.execCommand('styleWithCSS', false, true);
    document.execCommand(cmd, false, arg);
    emitChange();
  };

  return (
    <div className="rounded-lg border border-slate-300 focus-within:border-violet-500 overflow-hidden">
      <div className="flex flex-wrap items-center gap-0.5 px-1.5 py-1 border-b border-slate-200 bg-slate-50">
        <ToolBtn title="Negrita" onMouseDown={() => exec('bold')}><Bold size={14} /></ToolBtn>
        <ToolBtn title="Cursiva" onMouseDown={() => exec('italic')}><Italic size={14} /></ToolBtn>
        <ToolBtn title="Subrayado" onMouseDown={() => exec('underline')}><Underline size={14} /></ToolBtn>
        <span className="w-px h-4 bg-slate-300 mx-1" />
        <ToolBtn title="Alinear izquierda" onMouseDown={() => exec('justifyLeft')}><AlignLeft size={14} /></ToolBtn>
        <ToolBtn title="Centrar" onMouseDown={() => exec('justifyCenter')}><AlignCenter size={14} /></ToolBtn>
        <ToolBtn title="Alinear derecha" onMouseDown={() => exec('justifyRight')}><AlignRight size={14} /></ToolBtn>
        <span className="w-px h-4 bg-slate-300 mx-1" />
        {RICHTEXT_COLORS.map((c) => (
          <button
            key={c}
            type="button"
            title={c}
            onMouseDown={(e) => { e.preventDefault(); exec('foreColor', c); }}
            className="w-5 h-5 rounded-full border border-slate-300 shrink-0"
            style={{ backgroundColor: c }}
          />
        ))}
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emitChange}
        className="px-3 py-2 text-sm min-h-[80px] focus:outline-none [&_div]:min-h-[1em]"
      />
    </div>
  );
}

function IconBtn({ title, onClick, className = '', children, disabled = false }) {
  return (
    <button
      type="button"
      title={title}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={`inline-flex items-center justify-center w-7 h-7 rounded-md bg-transparent border-0 transition-colors ${disabled ? '' : 'cursor-pointer'} ${className}`}
    >
      {children}
    </button>
  );
}

